import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import "../../../css/Home.css";
import { registerCoach } from "../../../services/CoachService";
import { useAxiosInstance } from "../../../services/Interceptor";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { useDispatch } from "react-redux";
import {
  registerAccountStore,
  clearRegisterData,
} from "../../../store/slices/accountSlice";
import { states } from "../../../utils/PositionStateData";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { CheckIsEmailExist } from "../../../services/AccountService";
import {
  loadCaptchaEnginge,
  LoadCanvasTemplate,
  validateCaptcha,
} from "react-simple-captcha";

const CoachRegister = () => {
  const navigate = useNavigate();
  const apiInstance = useAxiosInstance();
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      university: "",
      city: "",
      state: "",
      role: "university",
    },
  });

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const useCoachApi = process.env.REACT_APP_SKIP_COACH_PAYMENT;
  const selectedState = watch("state");

  useEffect(() => {
    loadCaptchaEnginge(6);
  }, []);

  const checkIsEmailExist = async (email) => {
    try {
      const response = await CheckIsEmailExist(apiInstance, email);
      if (response?.data.data === false) {
        toast.warning("Email ID is already registered!", {
          position: "top-center",
        });
        return true;
      }
      return false;
    } catch (error) {
      toast.error("Failed to validate email address.", {
        position: "top-center",
      });
      return true;
    }
  };

  const onHandleSubmit = async (data) => {
    if (!data.captcha || !validateCaptcha(data.captcha)) {
      toast.error("Please complete the captcha correctly.", {
        position: "top-center",
      });
      return;
    }

    if (data.password !== data.confirmPassword) {
      toast.error("Passwords do not match!", { position: "top-center" });
      return;
    }

    const isEmailExist = await checkIsEmailExist(data.email);
    if (isEmailExist) return;

    setLoading(true);

    try {
      if (useCoachApi === "true") {
        const reg = await registerCoach(apiInstance, data);

        if (reg?.status === 200) {
          toast.success(
            "Coach registered successfully. Redirecting to Sign In…",
            {
              position: "top-right",
            },
          );

          reset();

          navigate("/sign-in");
        } else {
          toast.error("Registration failed. Please try again.", {
            position: "top-right",
          });
        }
      } else {
        dispatch(clearRegisterData());
        dispatch(registerAccountStore(data));
        localStorage.clear();
        localStorage.setItem("registeredAccount", JSON.stringify(data));

        toast.success("Please complete the payment.", {
          position: "top-right",
        });

        reset();
      }
    } catch (error) {
      toast.error(`Error Registering Coach: ${error?.message}`, {
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <h2 className="text-white undefined-600 uppercase font-bold px-6 text-xl">
        Registration
      </h2>

      {/* Info area */}

      <div className="alert flex flex-row items-center bg-blue-100 p-3 rounded border-b-2 border-blue-300 my-6 mx-6">
        <div className="alert-icon flex items-center bg-blue-100 border-2 border-blue-500 justify-center h-8 w-8 flex-shrink-0 rounded-full">
          <span className="text-blue-500">
            <svg fill="currentColor" viewBox="0 0 20 20" className="h-6 w-6">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              ></path>
            </svg>
          </span>
        </div>
        <div className="alert-content ml-4 w-full">
          <div className="alert-title font-semibold text-md text-blue-700">
            Have you already created your university profile?
          </div>
          <div className="alert-description text-sm text-blue-700">
            <a className="text-blue-700 hover:underline px-1" href="/sign-in">
              Sign In here
            </a>
          </div>
        </div>
      </div>

      <h4 className="text-xl font-bold   text-[#06D1E8] uppercase mx-6">
        Coach Registration
      </h4>
      <form
        className="container mx-auto px-4"
        onSubmit={handleSubmit(onHandleSubmit)}
      >
        <div className="px-2 mb-20">
          <div className="grid sm:grid-cols-2 gap-1 xl:gap-8 lg:gap-8">
            <div className="form-field mt-4">
              <label htmlFor="firstname">First Name</label>
              {
                <input
                  {...register("firstName", { required: true })}
                  placeholder="First Name"
                ></input>
              }
              {errors.firstName && (
                <span className="text-red-500">First name is required</span>
              )}
            </div>
            <div className="form-field mt-4">
              <label htmlFor="lastName">Last Name</label>
              <input
                {...register("lastName", { required: true })}
                placeholder="Last Name"
              ></input>
              {errors.lastName && (
                <span className="text-red-500">Last name is required</span>
              )}
            </div>
          </div>
          <div className="form-field mt-4">
            <label htmlFor="email">Email</label>
            <input
              {...register("email", {
                required: true,
                pattern: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/,
              })}
              placeholder="Email"
            ></input>
            {errors.email && (
              <span className="text-red-500">Valid email is required</span>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-1 xl:gap-8 lg:gap-8">
            <div className="form-field mt-4">
              <label htmlFor="password">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  {...register("password", {
                    required: true,
                    minLength: {
                      value: 8,
                      message: "Password must be at least 8 characters long",
                    },
                  })}
                  placeholder="Password"
                  className="px-4 py-2 w-full border rounded"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>
              {errors.password && (
                <span className="text-red-500">
                  {errors.password.message || "Password is required"}
                </span>
              )}
            </div>
            <div className="form-field mt-4">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  {...register("confirmPassword", { required: true })}
                  placeholder="Confirm Password"
                  className="px-4 py-2 w-full border rounded"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
                >
                  <FontAwesomeIcon
                    icon={showConfirmPassword ? faEyeSlash : faEye}
                  />
                </button>
              </div>
              {errors.confirmPassword && (
                <span className="text-red-500">
                  Confirm Password is required
                </span>
              )}
            </div>
          </div>
          <div className="form-field mt-4">
            <label htmlFor="university">University</label>
            <input
              {...register("university", { required: true })}
              placeholder="University"
            ></input>
            {errors.university && (
              <span className="text-red-500">University is required</span>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-1 xl:gap-8 lg:gap-8">
            <div className="form-field mt-4">
              <label htmlFor="city">City</label>
              <input
                {...register("city", { required: true })}
                placeholder="City"
              ></input>
              {errors.city && (
                <span className="text-red-500">City is required</span>
              )}
            </div>
            <div className="form-field mt-4 relative">
              <label htmlFor="state">State</label>
              <select
                name="state"
                className={`bg-gray-50 border border-gray-300 text-md rounded-md pr-10 appearance-none w-full

      ${selectedState ? "text-gray-900" : "text-gray-400"}

    `}
                {...register("state", { required: "State is required" })}
              >
                <option value="" disabled>
                  Select your state
                </option>

                {states.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
              {/* Arrow Icon */}
              <svg
                className="w-5 h-5 text-gray-200 absolute right-3 top-2/3 -translate-y-1/2 pointer-events-none"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
            <div>
              <input
                id="coach_captcha_input"
                name="captcha"
                {...register("captcha", { required: "Captcha is required" })}
                placeholder="Enter captcha"
                className="border p-4 w-full rounded-md bg-transparent text-white"
              />
              {errors.captcha && (
                <span className="text-red-500">{errors.captcha.message}</span>
              )}
            </div>
            <div className="bg-white rounded-md px-1 flex items-center justify-center">
              <LoadCanvasTemplate reloadColor="black" />
            </div>
          </div>
          <div className="form-field mt-4">{loading && <LoadingSpinner />}</div>
          <button
            type="submit"
            className="bg-red-500 text-white uppercase rounded-full px-8 py-2 text-center hover:bg-red-700 cursor-pointer mt-6"
          >
            Submit
          </button>
        </div>
      </form>
    </>
  );
};

export default CoachRegister;
